// src/components/AddPayment.jsx
import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { paymentActions } from "../redux/paymentSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SlArrowLeft } from "react-icons/sl";
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import "nepali-datepicker-reactjs/dist/index.css";
import api from "../utils/api";
import { ADToBS } from "bikram-sambat-js";
import { Button, notification } from "antd";
import { DownloadOutlined, FileImageOutlined } from "@ant-design/icons";
import SyncLoader from "react-spinners/SyncLoader";
import { MdOutlineSpeakerNotes } from "react-icons/md";
import {
  convertToEnglishDate,
  convertToNepaliDate,
} from "../components/FormatDate";

const AddPayment = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const [updating, setUpdating] = useState(false);

  const invoiceData = location.state;

  const [invoiceId, setInvoiceId] = useState(invoiceData?.invoiceId || null);
  const [amount, setAmount] = useState(invoiceData?.amount?.toString() || "");
  const [date, setDate] = useState("");
  const [slip, setSlip] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});
  const [dueAmount, setDueAmount] = useState(invoiceData?.amount || null);
  const [loadingDues, setLoadingDues] = useState(false);
  const fileInputRef = useRef(null);

  // Set default Nepali date
  useEffect(() => {
    const today = new Date();
    const nepaliDate = ADToBS(today);
    setDate(nepaliDate);
  }, []);

  // If not from invoice, fetch total dues
  useEffect(() => {
    if (!invoiceData) {
      setLoadingDues(true);
      api
        .get("/payments/mydues")
        .then((res) => {
          const dues = res?.data?.data?.due_amount;
          setDueAmount(dues);
          if (!amount) setAmount(dues?.toString() || "");
        })
        .catch(() => {
          notification.error({
            message: "Error",
            description: "Failed to fetch due amount",
          });
        })
        .finally(() => setLoadingDues(false));
    }
  }, [invoiceData, amount]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(file.type)) {
        notification.error({
          message: "Invalid File Type",
          description: "Only PNG, JPG, JPEG, and WEBP images are allowed.",
        });
        return;
      }

      if (file.size > maxSize) {
        notification.error({
          message: "File Too Large",
          description: "Image must be 2MB or smaller.",
        });
        return;
      }

      setSlip(file);
      setSlipPreview(URL.createObjectURL(file));
      // Clear slip error when valid file is selected
      setErrors(prev => ({ ...prev, slip: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      newErrors.amount = "Valid amount is required.";
    }
    
    if (!date) {
      newErrors.date = "Date is required.";
    }
    
    if (!slip) {
      newErrors.slip = "Payment slip is required.";
    } else {
      const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(slip.type)) {
        newErrors.slip = "Only PNG, JPG, JPEG, and WEBP are allowed.";
      } else if (slip.size > maxSize) {
        newErrors.slip = "Image must be 2MB or smaller.";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to parse backend errors
  const parseBackendErrors = (error) => {
    // Check if error has validation errors structure
    if (error?.errors && typeof error.errors === 'object') {
      const parsedErrors = {};
      
      // Loop through each field error
      Object.keys(error.errors).forEach(field => {
        const errorMessages = error.errors[field];
        // Join multiple error messages for the same field
        if (Array.isArray(errorMessages)) {
          parsedErrors[field] = errorMessages.join(' ');
        } else {
          parsedErrors[field] = errorMessages;
        }
      });
      
      return parsedErrors;
    }
    
    // Return empty object if no validation errors
    return {};
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    if (!validateForm()) return;

    setUpdating(true);
    const formData = new FormData();
    if (invoiceId) formData.append("invoice_id", invoiceId);
    else formData.append("resident_id", user?.user?.id);
    formData.append("amount", amount);
    formData.append("date", convertToEnglishDate(date));
    formData.append("slip", slip);
    formData.append("remarks", remarks);
    formData.append("status", "Pending");

    dispatch(paymentActions.addPayment(formData))
      .unwrap()
      .then(() => {
        notification.success({
          message: "Success",
          description: "Payment submitted successfully!",
        });
        setTimeout(() => navigate("/payment"), 1500);
      })
      .catch((err) => {
        console.error("Payment submission error:", err);
        
        // Parse backend validation errors
        const backendErrors = parseBackendErrors(err);
        
        if (Object.keys(backendErrors).length > 0) {
          // Set field-specific errors
          setErrors(backendErrors);
          
          // Show notification with the main message
          notification.error({
            message: err?.message || "Validation Error",
            description: Object.values(backendErrors)[0], // Show first error
            duration: 5,
          });
        } else {
          // Generic error handling
          notification.error({
            message: "Submission Failed",
            description: err?.message || err?.error || "Failed to submit payment. Please try again.",
            duration: 5,
          });
        }
      })
      .finally(() => setUpdating(false));
  };

  const triggerFileInput = () => fileInputRef.current.click();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 mb-14 pb-8 sm:pb-0 sm:mb-16">
      <div className="bg-gradient-to-r from-[#3F3F95] to-[#5858B8] px-4 py-4 w-full rounded-b-2xl shadow-lg mb-6">
        <div className="flex items-center justify-between">
          <Link to="/payment">
            <SlArrowLeft className="text-white text-xl hover:scale-110 transition-transform" />
          </Link>
          <h3 className="font-bold text-[22px] text-white tracking-wide">
            {invoiceData ? "Pay Invoice" : "Add Payment"}
          </h3>
          <div className="w-6" />
        </div>
      </div>

      <div className="px-4">
        {/* Invoice Info */}
        {invoiceData && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-indigo-200 rounded-2xl p-4 mb-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="bg-[#403F93] bg-opacity-10 p-2 rounded-lg">
                <MdOutlineSpeakerNotes className="text-[#403F93] text-xl" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Paying Invoice #{invoiceData.invoiceId}
                </p>
                <p className="text-xs text-gray-600">
                  Month:{" "}
                  <span className="font-medium">
                    {invoiceData.invoiceMonth}
                  </span>
                </p>
                <p className="text-xs text-gray-600">
                  Invoice Date:{" "}
                  <span className="font-medium">
                    {convertToNepaliDate(new Date(invoiceData.invoiceDate))}
                  </span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Amount Due:{" "}
                  <span className="font-bold text-[#403F93]">
                    Rs. {invoiceData.amount}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Due Amount */}
        {loadingDues ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5 flex items-center justify-center">
            <SyncLoader color="#3F3F95" size={8} />
            <span className="ml-3 text-sm text-gray-600">Loading dues...</span>
          </div>
        ) : (
          dueAmount !== null && (
            <div
              className={`border rounded-xl p-4 mb-5 ${
                invoiceData
                  ? "bg-blue-50 border-blue-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}
            >
              <p className="text-sm font-semibold text-gray-700">
                {invoiceData ? "Amount Due:" : "Total Due:"}{" "}
                <span className="text-lg text-[#403F93] font-bold">
                  Rs. {dueAmount}
                </span>
              </p>
            </div>
          )
        )}

        <form
          onSubmit={handlePaymentSubmit}
          className="bg-white rounded-2xl shadow-lg p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Payment Amount <span className="text-red-500">*</span>
            </label>
            <input
              className={`rounded-xl px-4 py-3 w-full border-2 ${
                errors.amount ? 'border-red-500' : 'border-gray-300'
              } focus:border-[#403F93] focus:outline-none`}
              type="number"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                // Clear error when user starts typing
                if (errors.amount) {
                  setErrors(prev => ({ ...prev, amount: undefined }));
                }
              }}
              placeholder="Enter amount"
            />
            {errors.amount && (
              <p className="text-red-500 text-xs mt-1">{errors.amount}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <NepaliDatePicker
              options={{ calenderLocale: "en", valueLocale: "en" }}
              value={date}
              onChange={(value) => {
                setDate(value);
                // Clear error when user selects date
                if (errors.date) {
                  setErrors(prev => ({ ...prev, date: undefined }));
                }
              }}
              className="w-full custom-date-picker"
            />
            {errors.date && (
              <p className="text-red-500 text-xs mt-1">{errors.date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Payment Screenshot <span className="text-red-500">*</span>
            </label>
            <div className={`rounded-xl px-4 py-3 w-full border-2 ${
              errors.slip ? 'border-red-500' : 'border-gray-300'
            } flex justify-between items-center hover:border-[#403F93]`}>
              <p className="text-gray-700 text-sm truncate flex-1 mr-2">
                {slip?.name || "Upload payment screenshot"}
              </p>
              <Button
                onClick={triggerFileInput}
                icon={<FileImageOutlined />}
                className="bg-gradient-to-r from-[#FEA733] to-[#FFB854] text-white border-0"
              >
                Upload
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            {errors.slip && (
              <p className="text-red-500 text-xs mt-1">{errors.slip}</p>
            )}
            {slipPreview && (
              <div className="mt-4 rounded-xl overflow-hidden border-2 border-gray-200 w-full h-60 flex items-center justify-center bg-gray-50">
                <img
                  src={slipPreview}
                  alt="Preview"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Remarks (Optional)
            </label>
            <textarea
              className="rounded-xl px-4 py-3 w-full border-2 border-gray-300 focus:border-[#403F93] resize-none"
              rows="3"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={updating}
            className="w-full bg-gradient-to-r from-[#3F3F95] to-[#5858B8] text-white font-semibold py-4 rounded-xl hover:from-[#353584] hover:to-[#4747A0] disabled:opacity-50 transition-all"
          >
            {updating ? (
              <span className="flex items-center justify-center gap-2">
                <SyncLoader color="#fff" size={8} /> Submitting...
              </span>
            ) : (
              "Submit Payment"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddPayment;